import React from "react";
import { Box, Theme, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import ListAddedIcon from "@mui/icons-material/AddBox";
import ListAllIcon from "@mui/icons-material/ListAlt";
import ListRemoveIcon from "@mui/icons-material/IndeterminateCheckBox";
import { VACATION_BID_TEST_ID } from "../testIds";

/** Which crew a rebuild would change, as a filter over the package crew table. */
export type CrewStatusFilterMode = "removed" | "all" | "added";

interface ICrewStatusFilterProps {
  displayMode: CrewStatusFilterMode;
  setDisplayMode: (mode: CrewStatusFilterMode) => void;
  addedCount: number;
  removedCount: number;
}

const CrewStatusFilterStyles = (theme: Theme): any => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "auto",
    margin: "0px 10px",
    "& .label": {
      color: theme.custom.forms.labels,
      marginRight: "8px",
    },
    "& .MuiToggleButtonGroup-root": {
      height: "26px",
    },
    "& .MuiToggleButton-root": {
      padding: "0px 10px",
      borderColor: theme.custom.forms.inputBorder,
      color: theme.custom.forms.labels,
      "& .MuiSvgIcon-root": { width: "16px", height: "16px" },
      "&.Mui-selected": {
        backgroundColor: theme.palette.action.selected,
        "&.removed": { color: theme.palette.warning.main },
        "&.all": { color: theme.palette.common.white },
        "&.added": { color: theme.palette.success.main },
      },
    },
  },
});

/**
 * Filters the crew table to the rows a rebuild would add or remove. One mode is always selected.
 */
export const CrewStatusFilter = (props: ICrewStatusFilterProps): JSX.Element => {
  const { displayMode, setDisplayMode, addedCount, removedCount } = props;

  const handleChange = (_event: React.MouseEvent<HTMLElement>, value: CrewStatusFilterMode | null): void => {
    // A null value is the selected button being clicked again; keep the current mode instead.
    if (value) {
      setDisplayMode(value);
    }
  };

  return (
    <Box className="crew_status_filter" sx={(theme) => CrewStatusFilterStyles(theme).root}>
      <Typography variant="caption" className="label">
        Status
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive={true}
        value={displayMode}
        onChange={handleChange}
        data-testid={VACATION_BID_TEST_ID.crewStatusFilter}
      >
        <ToggleButton className="removed" value="removed" aria-label="Show crew outside the selected groups">
          <Tooltip
            enterDelay={500}
            placement="top"
            title={`Show only crew in the package who are not in the selected groups (${removedCount})`}
          >
            <ListRemoveIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton className="all" value="all" aria-label="Show all crew">
          <Tooltip enterDelay={500} placement="top" title="Show all crew in the package and the selected groups">
            <ListAllIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton className="added" value="added" aria-label="Show crew being added">
          <Tooltip enterDelay={500} placement="top" title={`Show only crew being added to the package (${addedCount})`}>
            <ListAddedIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};
